import { Selecting, useToastProvider } from '@qualio/ui-components';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTags } from '../../../../../../../hooks/useTags';
import { ManageDocumentTagsModal } from '../../../../../ManageDocumentTagsModal/index';
import { useTableDataInvalidation } from '../../../hooks/useTableDataInvalidation';
import { hasRequiredPermissionsForAction } from '../../BulkActions';
import {
  BulkActionButton,
  BulkActionButtonProps,
} from '../BulkActionButton/index';

type ManageTagsButtonProps = Omit<
  BulkActionButtonProps,
  'isActionAllowed' | 'label' | 'onClick'
>;

export const ManageTagsButton = (props: ManageTagsButtonProps) => {
  const { t } = useTranslation();
  const { data: tags = [] } = useTags();
  const { showToast } = useToastProvider();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const selection = Selecting.useSelectionMaybe();
  const { selectedDocuments } = props;
  const { invalidate } = useTableDataInvalidation();
  const commonToastProps = {
    id: 'workspace-manage-tags-toast',
    replace: true,
  };

  const { modalDescription, successToastDescription } = useMemo(() => {
    const documentCount = selectedDocuments.length;
    const documentsText =
      documentCount > 1 ? t('labels.documents') : t('labels.document');
    return {
      modalDescription: `${t(
        'buttons.manageTags',
      )} for ${documentCount} ${documentsText}.`,
      successToastDescription: t('notifications.successfullyAppliedTagChanges'),
    };
  }, [selectedDocuments, t]);

  const canManageTags: BulkActionButtonProps['isActionAllowed'] = (
    user,
    statuses,
    _activeTab,
    requiredPermissions,
  ) => {
    if (
      !hasRequiredPermissionsForAction(requiredPermissions, user.permissions) ||
      !statuses.length
    ) {
      return false;
    }
    return true;
  };

  const onSuccessCallback = ({ removedTags }: { removedTags: number[] }) => {
    showToast({
      'data-cy': 'manage-tags-button-notify-success-toast',
      ...commonToastProps,
      status: 'success',
      title: t('notifications.tagsUpdated'),
      description: successToastDescription,
    });
    if (selection) {
      selection.setSelection({});
    }
    void invalidate(props.currentTab);
  };

  const onErrorCallback = () => {
    showToast({
      'data-cy': 'manage-tags-button-notify-error-toast',
      ...commonToastProps,
      status: 'error',
      title: t('notifications.error'),
      description: t('errors.failedToApplyChangesToEditors', { error: '' })
        .replace('editors', 'tags')
        .replace('. .', '.'),
    });
  };

  return (
    <>
      <BulkActionButton
        {...props}
        label={t('buttons.manageTags')}
        isActionAllowed={canManageTags}
        onClick={() => setIsModalOpen(true)}
      />
      <ManageDocumentTagsModal
        onClose={() => setIsModalOpen(false)}
        tags={tags}
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        modalDescription={modalDescription}
        onSuccess={onSuccessCallback}
        onError={onErrorCallback}
        selectedDocuments={selectedDocuments}
      />
    </>
  );
};
